import { arrayMove } from "@dnd-kit/sortable"
import { useCallback, useRef, useState } from "react"

import { patchUIPreferences } from "../../../User/services"
import type { UIPreferencesPatch } from "../../../User/redux/ui-preferences.types"
import {
  createEmptyFilterLabels,
  createEmptyFilters,
  parseActiveViewFilterLabels,
  parseActiveViewGlobalFilters,
  parseFilterLabels,
  parseGlobalFilters
} from "../../redux/globalFilters"
import type {
  DashboardConfigV3,
  DashboardView,
  FilterLabels,
  GlobalFilters
} from "../../redux/portfolio.types"
import type {
  DashboardConfigV4,
  DashboardViewV4,
  WidgetInstanceV4
} from "../../redux/widgets.types"

type LayoutVersion = 3 | 4
type InstanceMap = Record<string, WidgetInstanceV4>

interface UseDashboardLayoutOptions {
  version?: LayoutVersion
  resolveInstance?: (tileId: string) => WidgetInstanceV4 | null
  fixedInstances?: WidgetInstanceV4[]
  // Which `UIPreferences` key this instance reads/writes its layout under.
  // Defaults to the single-branch Home's own `"dashboard_config"` — Company
  // HQ passes a different key so editing one layout can never overwrite
  // the other.
  preferenceKey?: string
}

const DEFAULT_PREFERENCE_KEY = "dashboard_config"

// The default key writes the dedicated top-level `dashboard_config` field
// (existing, unchanged, so no migration for the accounts already using it);
// any other key rides the generic per-surface `preferences` bag instead of
// adding a new top-level field for every future alternate layout.
function buildPreferencesPatch(
  preferenceKey: string,
  config: DashboardConfigV3 | DashboardConfigV4
): UIPreferencesPatch {
  if (preferenceKey === DEFAULT_PREFERENCE_KEY) {
    return { dashboard_config: config }
  }
  return { preferences: { [preferenceKey]: config } }
}

export interface UseDashboardLayoutReturn {
  views: DashboardView[]
  activeView: { id: string; name: string } | null
  activeViewId: string | null
  defaultViewId: string | null
  isEditing: boolean
  order: string[]
  hidden: string[]
  globalFilters: GlobalFilters
  filterLabels: FilterLabels
  initFromConfig: (raw: unknown) => void
  persistGlobalFilters: (
    next: GlobalFilters,
    nextLabels?: FilterLabels,
    viewId?: string | null
  ) => Promise<void>
  createView: (name: string, from?: "current" | "default") => Promise<void>
  renameView: (id: string, name: string) => Promise<void>
  deleteView: (id: string) => Promise<void>
  setDefault: (id: string) => Promise<void>
  switchView: (id: string) => Promise<void>
  reorder: (activeId: string, overId: string) => void
  toggleHidden: (id: string) => void
  addTile: (id: string) => void
  addWidget: (instance: WidgetInstanceV4) => void
  registerInstances: (instances: WidgetInstanceV4[]) => void
  getInstance: (slug: string) => WidgetInstanceV4 | undefined
  startEdit: (effectiveOrder?: string[]) => void
  cancel: () => void
  save: () => Promise<void>
  restoreDefault: () => Promise<void>
}

function hydrateViewFilters<T extends DashboardView>(
  view: T,
  fallbackConfig: unknown
): T {
  return {
    ...view,
    globalFilters: Object.hasOwn(view, "globalFilters")
      ? parseGlobalFilters(view)
      : parseGlobalFilters(fallbackConfig),
    filterLabels: Object.hasOwn(view, "filterLabels")
      ? parseFilterLabels(view)
      : parseFilterLabels(fallbackConfig),
  }
}

function normalizeToV3(raw: unknown): DashboardConfigV3 {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>

    if (obj.version === 3 && Array.isArray(obj.views)) {
      const config = raw as DashboardConfigV3
      return {
        ...config,
        views: config.views.map((view) => hydrateViewFilters(view, config)),
      }
    }

    if (obj.version === 2) {
      return {
        version: 3,
        views: [
          {
            id: "default",
            name: "My View",
            order: Array.isArray(obj.order) ? (obj.order as string[]) : [],
            hidden: Array.isArray(obj.hidden) ? (obj.hidden as string[]) : [],
          },
        ],
        activeViewId: "default",
        defaultViewId: null,
      }
    }

    if (Array.isArray(obj.tiles)) {
      return {
        version: 3,
        views: [
          {
            id: "default",
            name: "My View",
            order: (obj.tiles as { id: string }[]).map((t) => t.id),
            hidden: [],
          },
        ],
        activeViewId: "default",
        defaultViewId: null,
      }
    }
  }

  return {
    version: 3,
    views: [{ id: "default", name: "My View", order: [], hidden: [] }],
    activeViewId: "default",
    defaultViewId: null,
  }
}

function toV4View(
  view: DashboardView,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[]
): DashboardViewV4 {
  const widgets = view.order
    .map((slug) => instances[slug])
    .filter((w): w is WidgetInstanceV4 => Boolean(w))
  const present = new Set(widgets.map((w) => w.slug))
  // The always-on charts live outside the reorderable order; append them so the
  // widgets endpoint resolves them for every view. An empty view is left empty —
  // that is the signal the backend reads as "reset me to the defaults".
  if (widgets.length) {
    for (const f of fixed) {
      if (!present.has(f.slug)) {
        widgets.push(instances[f.slug] ?? f)
        present.add(f.slug)
      }
    }
  }
  return {
    id: view.id,
    key: view.key ?? view.id,
    name: view.name,
    widgets,
    hidden: view.hidden,
    globalFilters: view.globalFilters ?? createEmptyFilters(),
    filterLabels: view.filterLabels ?? createEmptyFilterLabels(),
  }
}

function buildConfig(
  version: LayoutVersion,
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[]
): DashboardConfigV3 | DashboardConfigV4 {
  if (version === 4) {
    return {
      version: 4,
      views: views.map((v) => toV4View(v, instances, fixed)),
      activeViewId,
      defaultViewId,
    }
  }
  return { version: 3, views, activeViewId, defaultViewId }
}

interface LiftedLayout {
  views: DashboardView[]
  instances: InstanceMap
  activeViewId: string | null
  defaultViewId: string | null
}

function parseV4(config: DashboardConfigV4, fixed: WidgetInstanceV4[]): LiftedLayout {
  const instances: InstanceMap = {}
  for (const f of fixed) instances[f.slug] = f
  const fixedSlugs = new Set(fixed.map((f) => f.slug))
  const views: DashboardView[] = (config.views ?? []).map((v) => {
    const order: string[] = []
    for (const w of v.widgets ?? []) {
      instances[w.slug] = {
        id: w.id ?? w.slug,
        slug: w.slug,
        type: w.type,
        dataSource: w.dataSource,
        config: w.config ?? {},
      }
      if (!fixedSlugs.has(w.slug)) order.push(w.slug)
    }
    return {
      id: v.id,
      key: v.key ?? v.id,
      name: v.name,
      order,
      hidden: v.hidden ?? [],
      globalFilters: Object.hasOwn(v, "globalFilters")
        ? parseGlobalFilters(v)
        : parseGlobalFilters(config),
      filterLabels: Object.hasOwn(v, "filterLabels")
        ? parseFilterLabels(v)
        : parseFilterLabels(config),
    }
  })
  return {
    views,
    instances,
    activeViewId: config.activeViewId,
    defaultViewId: config.defaultViewId,
  }
}

function liftV3ToV4(
  base: DashboardConfigV3,
  resolveInstance: (tileId: string) => WidgetInstanceV4 | null,
  fixed: WidgetInstanceV4[]
): LiftedLayout {
  const instances: InstanceMap = {}
  for (const f of fixed) instances[f.slug] = f

  const views: DashboardView[] = base.views.map((v) => {
    const order: string[] = []
    for (const tileId of v.order) {
      const inst = resolveInstance(tileId)
      if (inst) {
        instances[inst.slug] = inst
        order.push(inst.slug)
      }
    }
    const hidden: string[] = []
    for (const tileId of v.hidden) {
      const inst = resolveInstance(tileId)
      if (inst) hidden.push(inst.slug)
    }
    return {
      id: v.id,
      key: v.id,
      name: v.name,
      order,
      hidden,
      globalFilters: v.globalFilters ?? createEmptyFilters(),
      filterLabels: v.filterLabels ?? createEmptyFilterLabels(),
    }
  })
  return {
    views,
    instances,
    activeViewId: base.activeViewId,
    defaultViewId: base.defaultViewId,
  }
}

function pickActiveId(
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null
): string | null {
  return (
    views.find((v) => v.id === activeViewId)?.id ??
    views.find((v) => v.id === defaultViewId)?.id ??
    views[0]?.id ??
    null
  )
}

async function commitConfig(
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null,
  order: string[],
  hidden: string[],
  globalFilters: GlobalFilters,
  filterLabels: FilterLabels,
  version: LayoutVersion,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[],
  preferenceKey: string
): Promise<void> {
  const updatedViews = views.map((v) =>
    v.id === activeViewId
      ? { ...v, order, hidden, globalFilters, filterLabels }
      : v
  )
  const config = buildConfig(
    version,
    updatedViews,
    activeViewId,
    defaultViewId,
    instances,
    fixed
  )
  const response = await patchUIPreferences(buildPreferencesPatch(preferenceKey, config))
  if (!response.ok) throw new Error("persist failed")
}

export function useDashboardLayout(
  options?: UseDashboardLayoutOptions
): UseDashboardLayoutReturn {
  const optsRef = useRef(options)
  optsRef.current = options
  const versionRef = useRef<LayoutVersion>(options?.version ?? 3)
  versionRef.current = options?.version ?? 3
  const fixedRef = useRef<WidgetInstanceV4[]>(options?.fixedInstances ?? [])
  fixedRef.current = options?.fixedInstances ?? []
  const preferenceKeyRef = useRef<string>(options?.preferenceKey ?? DEFAULT_PREFERENCE_KEY)
  preferenceKeyRef.current = options?.preferenceKey ?? DEFAULT_PREFERENCE_KEY
  const instancesRef = useRef<InstanceMap>({})

  const [views, setViews] = useState<DashboardView[]>([])
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [defaultViewId, setDefaultViewId] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [order, setOrder] = useState<string[]>([])
  const [hidden, setHidden] = useState<string[]>([])
  const [globalFilters, setGlobalFiltersState] = useState<GlobalFilters>(
    createEmptyFilters()
  )
  const [filterLabels, setFilterLabelsState] = useState<FilterLabels>(
    createEmptyFilterLabels()
  )

  const live = useRef({
    views,
    activeViewId,
    defaultViewId,
    order,
    hidden,
    globalFilters,
    filterLabels
  })
  live.current = {
    views,
    activeViewId,
    defaultViewId,
    order,
    hidden,
    globalFilters,
    filterLabels
  }

  const snapshot = useRef<{ order: string[]; hidden: string[] } | null>(null)
  const hasInitialized = useRef(false)

  const found = views.find((v) => v.id === activeViewId)
  const activeView = found ? { id: found.id, name: found.name } : null

  const initFromConfig = useCallback((raw: unknown) => {
    if (hasInitialized.current) return
    hasInitialized.current = true
    setIsEditing(false)
    snapshot.current = null

    if (versionRef.current === 4) {
      const opts = optsRef.current
      const fixed = opts?.fixedInstances ?? []
      const resolve = opts?.resolveInstance ?? (() => null)
      const isV4 =
        raw != null &&
        typeof raw === "object" &&
        !Array.isArray(raw) &&
        (raw as { version?: unknown }).version === 4
      const lifted = isV4
        ? parseV4(raw as DashboardConfigV4, fixed)
        : liftV3ToV4(normalizeToV3(raw), resolve, fixed)

      instancesRef.current = lifted.instances
      const resolvedId = pickActiveId(
        lifted.views,
        lifted.activeViewId,
        lifted.defaultViewId
      )
      const activeV = lifted.views.find((v) => v.id === resolvedId)
      setViews(lifted.views)
      setActiveViewId(resolvedId)
      setDefaultViewId(lifted.defaultViewId)
      setOrder(activeV?.order ?? [])
      setHidden(activeV?.hidden ?? [])
      setGlobalFiltersState(
        activeV ? parseGlobalFilters(activeV) : parseActiveViewGlobalFilters(raw)
      )
      setFilterLabelsState(
        activeV ? parseFilterLabels(activeV) : parseActiveViewFilterLabels(raw)
      )
      return
    }

    const config = normalizeToV3(raw)
    const resolvedId = pickActiveId(
      config.views,
      config.activeViewId,
      config.defaultViewId
    )
    const activeV = config.views.find((v) => v.id === resolvedId)
    setViews(config.views)
    setActiveViewId(resolvedId)
    setDefaultViewId(config.defaultViewId)
    setOrder(activeV?.order ?? [])
    setHidden(activeV?.hidden ?? [])
    setGlobalFiltersState(
      activeV ? parseGlobalFilters(activeV) : parseActiveViewGlobalFilters(raw)
    )
    setFilterLabelsState(
      activeV ? parseFilterLabels(activeV) : parseActiveViewFilterLabels(raw)
    )
  }, [])

  const persistGlobalFilters = useCallback(
    async (
      next: GlobalFilters,
      nextLabels?: FilterLabels,
      requestedViewId?: string | null
    ) => {
      const {
        views: v,
        activeViewId: aid,
        defaultViewId: did,
        order: o,
        hidden: h,
        filterLabels: currentLabels,
      } = live.current
      const targetId = requestedViewId ?? aid
      const targetView = v.find((view) => view.id === targetId)
      if (!targetView) return

      const labels =
        nextLabels ??
        (targetId === aid ? currentLabels : parseFilterLabels(targetView))
      if (targetId !== aid) {
        const updatedViews = v.map((view) =>
          view.id === targetId
            ? { ...view, globalFilters: next, filterLabels: labels }
            : view
        )
        setViews(updatedViews)
        const config = buildConfig(
          versionRef.current,
          updatedViews,
          aid,
          did,
          instancesRef.current,
          fixedRef.current
        )
        const response = await patchUIPreferences(
          buildPreferencesPatch(preferenceKeyRef.current, config)
        )
        if (!response.ok) throw new Error("persist failed")
        return
      }

      setGlobalFiltersState(next)
      setFilterLabelsState(labels)
      // While editing, live order/hidden is an unsaved draft; persist the pre-edit
      // snapshot so a filter change never commits the draft ahead of Save/Cancel.
      const committed = snapshot.current ?? { order: o, hidden: h }
      const updatedViews = v.map((view) =>
        view.id === aid
          ? {
              ...view,
              order: committed.order,
              hidden: committed.hidden,
              globalFilters: next,
              filterLabels: labels,
            }
          : view
      )
      setViews(updatedViews)
      await commitConfig(
        updatedViews, aid, did, committed.order, committed.hidden, next, labels,
        versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
      )
    },
    []
  )

  const createView = useCallback(
    async (name: string, from: "current" | "default" = "current") => {
      const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current

      const savedViews = v.map((view) =>
        view.id === aid
          ? { ...view, order: o, hidden: h, globalFilters: g, filterLabels: fl }
          : view
      )
      const srcView =
        from === "default"
          ? savedViews.find((x) => x.id === did)
          : savedViews.find((x) => x.id === aid)
      const srcOrder = srcView ? srcView.order : from === "default" ? [] : o
      const srcHidden = srcView ? srcView.hidden : from === "default" ? [] : h
      const srcFilters = srcView
        ? parseGlobalFilters(srcView)
        : from === "default"
          ? createEmptyFilters()
          : g
      const srcLabels = srcView
        ? parseFilterLabels(srcView)
        : from === "default"
          ? createEmptyFilterLabels()
          : fl

      const id = crypto.randomUUID()
      const newView: DashboardView = {
        id,
        key: id,
        name,
        order: srcOrder,
        hidden: srcHidden,
        globalFilters: srcFilters,
        filterLabels: srcLabels,
      }
      const updatedViews = [...savedViews, newView]

      setViews(updatedViews)
      setActiveViewId(id)
      setOrder(srcOrder)
      setHidden(srcHidden)
      setGlobalFiltersState(srcFilters)
      setFilterLabelsState(srcLabels)
      setIsEditing(true)
      snapshot.current = { order: srcOrder, hidden: srcHidden }

      await commitConfig(
        updatedViews, id, did, srcOrder, srcHidden, srcFilters, srcLabels,
        versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
      )
    },
    []
  )

  const renameView = useCallback(async (id: string, name: string) => {
    const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    const updatedViews = v.map((x) => ({
      ...x,
      ...(x.id === id ? { name } : {}),
      ...(x.id === aid
        ? { order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : {}),
    }))
    setViews(updatedViews)
    await commitConfig(
      updatedViews, aid, did, o, h, g, fl,
      versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
    )
  }, [])

  const deleteView = useCallback(async (id: string) => {
    const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    if (v.length <= 1) return

    const savedViews = v.map((view) =>
      view.id === aid
        ? { ...view, order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : view
    )
    const updatedViews = savedViews.filter((x) => x.id !== id)
    const nextDefaultId = did === id ? (updatedViews[0]?.id ?? null) : did

    let nextActiveId = aid
    let nextOrder = o
    let nextHidden = h

    if (aid === id) {
      const fallback = updatedViews[0]
      nextActiveId = fallback?.id ?? null
      nextOrder = fallback?.order ?? []
      nextHidden = fallback?.hidden ?? []
      const nextFilters = fallback
        ? parseGlobalFilters(fallback)
        : createEmptyFilters()
      const nextLabels = fallback
        ? parseFilterLabels(fallback)
        : createEmptyFilterLabels()
      setActiveViewId(nextActiveId)
      setOrder(nextOrder)
      setHidden(nextHidden)
      setGlobalFiltersState(nextFilters)
      setFilterLabelsState(nextLabels)
      setIsEditing(false)
      snapshot.current = null
    }

    setViews(updatedViews)
    setDefaultViewId(nextDefaultId)
    const active = updatedViews.find((view) => view.id === nextActiveId)
    const nextFilters = active ? parseGlobalFilters(active) : createEmptyFilters()
    const nextLabels = active ? parseFilterLabels(active) : createEmptyFilterLabels()
    await commitConfig(
      updatedViews, nextActiveId, nextDefaultId, nextOrder, nextHidden,
      nextFilters, nextLabels,
      versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
    )
  }, [])

  const setDefault = useCallback(async (id: string) => {
    const { views: v, activeViewId: aid, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    setDefaultViewId(id)
    await commitConfig(
      v, aid, id, o, h, g, fl,
      versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
    )
  }, [])

  const switchView = useCallback(async (id: string) => {
    const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    if (id === aid) return
    const nextView = v.find((x) => x.id === id)
    if (!nextView) return

    const savedViews = v.map((x) =>
      x.id === aid
        ? { ...x, order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : x
    )
    const resolvedNext = savedViews.find((x) => x.id === id)!
    const nextFilters = parseGlobalFilters(resolvedNext)
    const nextLabels = parseFilterLabels(resolvedNext)

    setViews(savedViews)
    setActiveViewId(id)
    setOrder(resolvedNext.order)
    setHidden(resolvedNext.hidden)
    setGlobalFiltersState(nextFilters)
    setFilterLabelsState(nextLabels)

    try {
      await commitConfig(
        savedViews, id, did, resolvedNext.order, resolvedNext.hidden,
        nextFilters, nextLabels,
        versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
      )
    } catch (err) {
      setViews(v)
      setActiveViewId(aid)
      setOrder(o)
      setHidden(h)
      setGlobalFiltersState(g)
      setFilterLabelsState(fl)
      throw err
    }
  }, [])

  const startEdit = useCallback((effectiveOrder?: string[]) => {
    const { order: o, hidden: h } = live.current
    const seedOrder = o.length ? o : (effectiveOrder ?? o)
    if (!o.length && effectiveOrder?.length) {
      setOrder(seedOrder)
    }
    snapshot.current = { order: seedOrder, hidden: h }
    setIsEditing(true)
  }, [])

  const cancel = useCallback(() => {
    if (snapshot.current) {
      setOrder(snapshot.current.order)
      setHidden(snapshot.current.hidden)
      snapshot.current = null
    }
    setIsEditing(false)
  }, [])

  const reorder = useCallback((activeId: string, overId: string) => {
    setOrder((prev) => {
      const oldIdx = prev.indexOf(activeId)
      const newIdx = prev.indexOf(overId)
      if (oldIdx === -1 || newIdx === -1) return prev
      return arrayMove(prev, oldIdx, newIdx)
    })
  }, [])

  const toggleHidden = useCallback((id: string) => {
    setHidden((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }, [])

  const addTile = useCallback((id: string) => {
    setOrder((prev) => (prev.includes(id) ? prev : [...prev, id]))
    setHidden((prev) => prev.filter((x) => x !== id))
  }, [])

  const addWidget = useCallback((instance: WidgetInstanceV4) => {
    instancesRef.current[instance.slug] = instance
    setOrder((prev) => (prev.includes(instance.slug) ? prev : [...prev, instance.slug]))
    setHidden((prev) => prev.filter((x) => x !== instance.slug))
  }, [])

  const registerInstances = useCallback((instances: WidgetInstanceV4[]) => {
    for (const inst of instances) {
      if (!instancesRef.current[inst.slug]) instancesRef.current[inst.slug] = inst
    }
  }, [])

  const getInstance = useCallback(
    (slug: string) => instancesRef.current[slug],
    []
  )

  const save = useCallback(async () => {
    const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    const updatedViews = v.map((x) =>
      x.id === aid
        ? { ...x, order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : x
    )
    const config = buildConfig(
      versionRef.current, updatedViews, aid, did, instancesRef.current, fixedRef.current
    )
    const response = await patchUIPreferences(
      buildPreferencesPatch(preferenceKeyRef.current, config)
    )
    if (!response.ok) throw new Error("save failed")
    setViews(updatedViews)
    setIsEditing(false)
    snapshot.current = null
  }, [])

  // Restores the out-of-the-box layout, not another saved view: an empty view is
  // what makes the backend re-resolve role → company → system defaults.
  const restoreDefault = useCallback(async () => {
    const { views: v, activeViewId: aid, defaultViewId: did, order: o, hidden: h, globalFilters: g, filterLabels: fl } = live.current
    if (aid == null) return
    const emptyFilters = createEmptyFilters()
    const emptyLabels = createEmptyFilterLabels()
    setOrder([])
    setHidden([])
    setGlobalFiltersState(emptyFilters)
    setFilterLabelsState(emptyLabels)
    const updatedViews = v.map((view) =>
      view.id === aid
        ? {
            ...view,
            order: [],
            hidden: [],
            globalFilters: emptyFilters,
            filterLabels: emptyLabels,
          }
        : view
    )
    setViews(updatedViews)
    setIsEditing(false)
    snapshot.current = null
    try {
      await commitConfig(
        updatedViews, aid, did, [], [], emptyFilters, emptyLabels,
        versionRef.current, instancesRef.current, fixedRef.current, preferenceKeyRef.current
      )
    } catch (err) {
      setViews(v)
      setOrder(o)
      setHidden(h)
      setGlobalFiltersState(g)
      setFilterLabelsState(fl)
      throw err
    }
  }, [])

  return {
    views,
    activeView,
    activeViewId,
    defaultViewId,
    isEditing,
    order,
    hidden,
    globalFilters,
    filterLabels,
    initFromConfig,
    persistGlobalFilters,
    createView,
    renameView,
    deleteView,
    setDefault,
    switchView,
    reorder,
    toggleHidden,
    addTile,
    addWidget,
    registerInstances,
    getInstance,
    startEdit,
    cancel,
    save,
    restoreDefault,
  }
}
